/*
  (c) 2025 Fluxonix Inc.
*/

// src/pages/SignupVerify.jsx

import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthProvider";

export default function SignupVerify() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const email = location.state?.email;
  const signupMode = location.state?.signupMode || "trial";

  if (!email) {
  return (
    <Box sx={{ maxWidth: 480, mx: "auto", mt: 8, textAlign: "center" }}>
      <Typography variant="h6" color="error">
        Missing signup context. Please restart your trial signup.
      </Typography>
      <Button variant="outlined" onClick={() => navigate("/")}>
        Back to Home
      </Button>
    </Box>
  );
}

  const [otp, setOtp] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleVerify = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await api.post("/auth/verify-otp", { 
        email, 
        otp,
      signup_mode: signupMode,
     });
      const token = res.data?.access_token;
      if (!token) throw new Error("Missing token from OTP verification.");

      // Optionally store OpenAI API key for later use (or skip this if not ready)
      if (apiKey) {
        await api.post(
          "/api/user/openai-key",
          { api_key: apiKey },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      // Fetch user profile and login
      const userRes = await api.get("/auth/me", {
        headers: { Authorization: `Bearer ${token}` },
      });

      login(userRes.data, token);
      navigate("/secure/voice");
    } catch (err) {
      setError(err.response?.data?.detail || "Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 480, mx: "auto", mt: 8 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            {signupMode === "sandbox" ? "Confirm Pilot Access" : "Verify Your Email"}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter the OTP sent to <strong>{email}</strong>.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <TextField
            label="OTP Code"
            fullWidth
            margin="normal"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
          />

          {signupMode !== "sandbox" && (
            <TextField
              label="OpenAI API Key (optional)"
              fullWidth
              margin="normal"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="sk-..."
            />
          )}

          <Box mt={3}>
            <Button
              variant="contained"
              fullWidth
              disabled={loading}
              onClick={handleVerify}
            >
              {loading ? <CircularProgress size={24} /> : "Verify & Start"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}